import { TEAM } from '../MatchAuthority.js';

/**
 * Team Deathmatch.
 *
 * The simplest mode there is, and the one worth writing first for exactly that
 * reason: it exercises every seam the authority grew — teams, kill attribution,
 * per-side scoring, a score limit, team spawns — without needing an objective
 * system on top. If TDM cannot be written as one small file, the extraction was
 * wrong and Domination has no chance.
 *
 * ## What it actually decides
 *
 * A kill is a point for the killer's side. A team kill is a point *off* the
 * killer's side, which is the standard treatment and is the only reason friendly
 * fire is worth having as a switch at all — without a penalty it is a free way to
 * deny the other team a kill. Suicide costs your side a point for the same reason.
 *
 * Everything else — how a bullet finds a body, what a headshot multiplies, how the
 * player respawns — belongs to systems that have never heard of this file.
 */

export const TUNING = {
  scoreLimit: 75,
  /** Bots per side. The player fills one slot on ALPHA, so ALPHA fields one fewer. */
  squad: 6,
  /** Seconds. Zero means no clock. */
  timeLimit: 600,
  /** A kill on your own side. Negative, and it does not credit the victim's team. */
  teamKillPenalty: -1,
};

export class TeamDeathmatch {
  constructor(opts = {}) {
    this.id = 'tdm';
    this.name = 'チームデスマッチ';
    /** This mode decides when the match ends; the director's kill target stands down. */
    this.ownsVictory = true;
    this.scoreLimit = opts.scoreLimit ?? TUNING.scoreLimit;
    this.timeLimit = opts.timeLimit ?? TUNING.timeLimit;
    this.squad = opts.squad ?? TUNING.squad;

    this.friendlyFire = opts.friendlyFire ?? false;
    this.selfDamage = true;

    this.score = { [TEAM.ALPHA]: 0, [TEAM.BRAVO]: 0 };
    this.elapsed = 0;
  }

  attach(match) {
    this.match = match;
    this.events = match.events;
    this._publish();
  }

  /**
   * Bots per side. ALPHA is one short because the human is standing in that slot —
   * a mode that fields six bots a side and a player makes one team seven-strong,
   * and TDM's entire premise is that the sides are even.
   */
  get roster() {
    return { [TEAM.ALPHA]: Math.max(0, this.squad - 1), [TEAM.BRAVO]: this.squad };
  }

  canDamage(src, dst) {
    if (this.friendlyFire) return true;
    if (src.team === TEAM.NONE || dst.team === TEAM.NONE) return true;
    return src.team !== dst.team;
  }

  canSplash() {
    return true;
  }

  onKill(res) {
    const victim = res.target.team;
    const killer = res.source?.team ?? null;

    if (killer === null) {
      // Nobody's kill: the map killed them. Costs their own side, so walking off
      // the edge is not a free way to deny the enemy a point.
      this._add(victim, -1);
    } else if (killer === victim) {
      this._add(killer, TUNING.teamKillPenalty);
    } else {
      this._add(killer, 1);
    }
    this._publish();
  }

  _add(team, n) {
    if (team === TEAM.NONE || team == null) return;
    this.score[team] = Math.max(0, (this.score[team] ?? 0) + n);
  }

  /**
   * Spawn on your own half of the fight: the friendly spawn point furthest from
   * the nearest live enemy. Returning null would hand it back to the director's
   * search, which is the same idea with the player hard-coded as the only person
   * worth spawning away from.
   */
  pickSpawn(reg, ctx) {
    if (!reg) return null;
    const spawns = ctx.ai?.spawnPoints;
    if (!spawns?.length) return null;

    const foes = [];
    for (const r of this.match.actors.values()) {
      if (r.dead || r.team === reg.team || r.team === TEAM.NONE) continue;
      if (r.actor?.position) foes.push(r.actor);
    }
    if (!foes.length) return null;

    let best = null;
    let bestD = -Infinity;
    for (const s of spawns) {
      let d = Infinity;
      for (const f of foes) d = Math.min(d, s.distanceTo(f.position));
      if (d > bestD) {
        bestD = d;
        best = s;
      }
    }
    return best;
  }

  tick(dt) {
    if (this.timeLimit > 0) this.elapsed += dt;
  }

  checkWin() {
    for (const team of [TEAM.ALPHA, TEAM.BRAVO]) {
      if (this.score[team] >= this.scoreLimit) return this._verdict(team, 'score');
    }
    if (this.timeLimit > 0 && this.elapsed >= this.timeLimit) {
      const a = this.score[TEAM.ALPHA];
      const b = this.score[TEAM.BRAVO];
      if (a === b) return { team: TEAM.NONE, reason: 'time', key: 'banner.draw', win: false };
      return this._verdict(a > b ? TEAM.ALPHA : TEAM.BRAVO, 'time');
    }
    return null;
  }

  _verdict(team, reason) {
    const win = team === TEAM.ALPHA;
    // A key, not a word: the banner translates it. See `Banner`.
    return { team, reason, win, key: win ? 'banner.victory' : 'banner.defeat' };
  }

  _publish() {
    this.events?.emit('match:score', {
      mode: this.id,
      score: { ...this.score },
      limit: this.scoreLimit,
      elapsed: this.elapsed,
      timeLimit: this.timeLimit,
    });
  }
}
